import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import UAParser from 'ua-parser-js'

import { checkBusinessHours } from '@lyrahealth-inc/shared-app-logic/src/features/careNavigator/utils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'

const RETRY_TIMEOUT_MS = 10000 // 10 seconds

import { getId } from '../../../data/user/userSelectors'
import { getJwtToken } from '../../http/data/jwt'
import { logToSentry, LOG_CATEGORY } from '../../utils/userUtils'

interface UseSierraChatProps {
  isInternational: boolean
  lyraId: string
  userFirstName: string
  userLastName: string
  userEmail: string
  customerName: string
  customerId: number
  events: { onOnline: () => void; onOffline: () => void; onDisabled: () => void }
}

interface UseSierraChatReturn {
  loadSierraChat: () => void
}

declare global {
  interface Window {
    sierra: any // This is attached to the global window by Sierra's script and doesn't benefit us to type
    __sierraIntegration?: {
      initialized: boolean
      onConversationStart: ((opts: { conversationID: string }) => void) | null
    }
  }
}

/**
 * Custom hook to handle Sierra chat initialization and interaction
 * Initializes Sierra chat when the feature flag is enabled and provides a method to open the chat modal
 */
export const useSierraChat = ({
  isInternational,
  lyraId,
  userFirstName,
  userLastName,
  userEmail,
  customerName,
  customerId,
  events,
}: UseSierraChatProps): UseSierraChatReturn => {
  const {
    shouldUseSierraChat,
    shouldSierraChatBeEnabledOutsideOfBusinessHours,
    shouldSierraChatAllowUserAuthentication,
  } = useFlags()
  const userLyraId = useSelector(getId)

  useEffect(() => {
    // Initialize Sierra if the main flag is enabled, or if the feature flag to force this is enabled
    const shouldInitializeSierra = shouldUseSierraChat || shouldSierraChatBeEnabledOutsideOfBusinessHours

    if (!shouldInitializeSierra || isInternational) {
      return
    }

    // Check if Sierra script is already loaded or if config has already been set
    if (window.sierra || (window as any).__sierraIntegration?.initialized) {
      return
    }

    // Initialize Sierra integration object and mark as initialized to prevent duplicate initialization
    // This must be set synchronously before the async initialization to prevent race conditions
    ;(window as any).__sierraIntegration = {
      initialized: true,
      onConversationStart: null,
    }

    const initializeSierra = async () => {
      // Fetch JWT token when initializing Sierra if authentication is enabled
      let token: string | null = null
      if (shouldSierraChatAllowUserAuthentication) {
        // Only attempt to get JWT if there is a valid user lyraId
        if (userLyraId) {
          try {
            token = await getJwtToken()
          } catch {
            // If we get an error, we'll assume the user isn't logged in
            // In that case we don't need to do anything but suppress the error as that's an expected use case for
            // the user to try to start a chat when not logged in
          }
        }
      }

      // Create and append Sierra script to page to load the app itself
      const script = document.createElement('script')
      script.type = 'module'
      script.src = `https://sierra.chat/agent/${process.env.SIERRA_CHAT_KEY}/embed${
        process.env.SIERRA_CHAT_ENV ? `/${process.env.SIERRA_CHAT_ENV}` : ''
      }`
      script.defer = true
      document.body.appendChild(script)

      // Compose a key-value pair of user data to pass to Sierra. If the user requests a transfer to a
      // chat agent, Sierra will pass this to the agent to allow them to identify the user immediately.
      const userAgent = new UAParser(window.navigator.userAgent)
      const userBrowser = userAgent.getResult().browser.name
      const userOS = userAgent.getOS().name

      // Define a global callback function for Sierra to call when a conversation starts,
      // giving it a passthrough to invoke our Sentry logging utility method
      ;(window as any).__sierraIntegration.onConversationStart = (opts: { conversationID: string }) => {
        const sourceCategory =
          process.env.NODE_ENV === 'production' ? LOG_CATEGORY.PROD_DEBUG : LOG_CATEGORY.STAGING_DEBUG
        logToSentry(sourceCategory, {
          userId: lyraId,
          message: 'Sierra chat conversation started',
          type: 'info',
          conversationID: opts.conversationID,
          customerName,
          customerId,
        })
      }

      // Add behavior configuration along with memory variables / secrets (state) to Sierra via `innerHTML` per their documentation
      // Secrets are PII / otherwise sensitive (encrypted on Sierra's side), variables are not
      // All values are properly escaped using JSON.stringify to prevent script injection
      const configScript = document.createElement('script')
      configScript.type = 'text/javascript'
      configScript.innerHTML = `
        var sierraConfig = {
          display: 'corner',
          persistence: 'cookie',
          hideOnClose: true,
          canStartNewChat: true,
          canEndConversation: true,
          onConversationStart: function(opts) {
            if (window.__sierraIntegration?.onConversationStart) {
              window.__sierraIntegration.onConversationStart(opts);
            }
          },
          variables: {
            Microsite: ${JSON.stringify(customerName)},
            MicrositeURL: ${JSON.stringify(window.location.origin)},
            Browser: ${JSON.stringify(userBrowser)},
            OS: ${JSON.stringify(userOS)}
          },
          secrets: {
            First_Name: ${JSON.stringify(userFirstName)},
            Last_Name: ${JSON.stringify(userLastName)},
            Email: ${JSON.stringify(userEmail)},
            Lyra_ID: ${JSON.stringify(lyraId)},
            CustomerId: ${JSON.stringify(String(customerId))},
            AUTH_TOKEN: ${JSON.stringify(token)}
          }
        }
      `
      document.body.appendChild(configScript)

      // Cleanup function to remove scripts when component unmounts or flag changes
      return () => {
        if (script.parentNode) {
          script.parentNode.removeChild(script)
        }
        if (configScript.parentNode) {
          configScript.parentNode.removeChild(configScript)
        }
        // Note: We intentionally do NOT reset __sierraIntegration.initialized here
        // Once Sierra is initialized, it should remain initialized for the page lifetime
        // to prevent duplicate initialization and double-firing of callbacks
      }
    }

    const cleanup = initializeSierra()

    return () => {
      cleanup.then((cleanupFn) => {
        if (cleanupFn) {
          cleanupFn()
        }
      })
    }
  }, [
    shouldUseSierraChat,
    shouldSierraChatBeEnabledOutsideOfBusinessHours,
    shouldSierraChatAllowUserAuthentication,
    isInternational,
    lyraId,
    userFirstName,
    userLastName,
    userEmail,
    customerName,
    customerId,
    userLyraId,
  ])

  const retryStartTimeRef = useRef<number | null>(null)

  const loadSierraChat = () => {
    const { onOnline, onOffline, onDisabled } = events

    if (window.sierra && window.sierra.openChatModal) {
      retryStartTimeRef.current = null // Reset on success
      const online = checkBusinessHours()
      // Allow Sierra to open if within business hours OR if the feature flag to force this is enabled, and within non-test environment
      if ((online || shouldSierraChatBeEnabledOutsideOfBusinessHours) && !window.Cypress) {
        onOnline()
        window.sierra.openChatModal()
      } else {
        onOffline()
      }
    } else if ((window as $TSFixMe).__sierraIntegration?.initialized) {
      // Mobile --> mobile web proxy takes longer to initialize; add retry for robustness to allow Sierra to finish loading
      // Initialize retry start time if not already set
      if (retryStartTimeRef.current === null) {
        retryStartTimeRef.current = Date.now()
      }

      // Check if we've exceeded the retry timeout
      if (Date.now() - retryStartTimeRef.current < RETRY_TIMEOUT_MS) {
        setTimeout(() => loadSierraChat(), 100)
      } else {
        retryStartTimeRef.current = null // Reset for future attempts
        onDisabled()
      }
    } else {
      onDisabled()
    }
  }

  return {
    loadSierraChat,
  }
}
